import { useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useSubscriptionAccess } from '@/hooks/useSubscriptionAccess';
import { useSubscription } from '@/hooks/useSubscription';
import { X, Crown, Zap, Gift } from 'lucide-react';

export function SubscriptionBanner() {
  const [dismissed, setDismissed] = useState(false);
  const { isSubscribed, currentPlan } = useSubscriptionAccess();
  const { createCheckout } = useSubscription();

  // Don't show banner if user is already subscribed or has dismissed it
  if (isSubscribed || dismissed) {
    return null;
  }

  const handleUpgrade = (plan: string) => {
    createCheckout(plan);
  };

  return (
    <Alert className="border-l-4 border-l-primary bg-gradient-to-r from-primary/5 to-primary-glow/5 relative">
      <Button
        variant="ghost"
        size="sm"
        className="absolute top-2 right-2 h-6 w-6 p-0"
        onClick={() => setDismissed(true)}
      >
        <X className="h-4 w-4" />
      </Button>
      
      <div className="flex items-center gap-2 mb-2">
        <Gift className="h-5 w-5 text-primary" />
        <div className="flex items-center gap-2">
          <span className="font-semibold">Kostenloses Konto</span>
          <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
            Limited Features
          </Badge>
        </div>
      </div>
      
      <AlertDescription className="space-y-3">
        <p className="text-sm">
          Sie nutzen aktuell die kostenlose Version mit begrenzten Funktionen. 
          Upgraden Sie für erweiterte Features und höhere Limits.
        </p>
        
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <div className="text-xs p-2 bg-white rounded-lg border">
            <div className="font-medium">Free Plan</div>
            <div className="text-muted-foreground">
              • 25 Kunden<br/>
              • 100 Stempel/Monat<br/>
              • Basis-Features
            </div>
          </div>
          
          <div className="text-xs p-2 bg-blue-50 rounded-lg border border-blue-200">
            <div className="font-medium text-blue-800">Basic Plan - 29€</div>
            <div className="text-blue-600">
              • 100 Kunden<br/>
              • 500 Stempel/Monat<br/>
              • Analytics
            </div>
          </div>
          
          <div className="text-xs p-2 bg-purple-50 rounded-lg border border-purple-200">
            <div className="font-medium text-purple-800">Pro Plan - 79€</div>
            <div className="text-purple-600">
              • 500 Kunden<br/>
              • 2000 Stempel/Monat<br/>
              • Alle Features
            </div>
          </div>
        </div>
        
        <div className="flex gap-2 pt-2">
          <Button 
            size="sm"
            onClick={() => handleUpgrade('basic')}
            className="flex items-center gap-1"
          >
            <Zap className="h-3 w-3" />
            Basic starten
          </Button>
          <Button 
            size="sm"
            onClick={() => handleUpgrade('pro')}
            className="flex items-center gap-1 bg-gradient-to-r from-purple-600 to-purple-800 hover:from-purple-700 hover:to-purple-900"
          >
            <Crown className="h-3 w-3" />
            Pro starten
          </Button>
        </div>
        
        <p className="text-xs text-muted-foreground">
          🎁 Alle Pläne mit 14 Tagen kostenloser Testphase
        </p>
      </AlertDescription>
    </Alert>
  );
}